"use client";

import { trpc } from "@calcom/trpc/react";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { useMemo } from "react";

/**
 * FEAT-087: rutas excluidas del gate de facturación.
 *
 * - `/auth/*`, `/api/*`, `/_next/*` → ya las maneja el middleware Next.
 * - `/onboarding/*` → durante onboarding el usuario está justamente capturando
 *   estos datos; bloquear con el gate sería ciclico.
 * - `/payments/*` → ruta pública de pago (Webpay redirect).
 */
const EXCLUDED_PATH_PREFIXES = [
  "/auth",
  "/api",
  "/_next",
  "/onboarding",
  "/payments",
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
];

function isExcludedPath(pathname: string | null): boolean {
  if (!pathname) return true;
  return EXCLUDED_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export type UseBillingProfileGateResult = {
  /** El gate modal debe mostrarse (autenticado + path no excluido + perfil incompleto). */
  shouldShowGate: boolean;
  /** La query del status sigue cargando — no decidir aún. */
  isLoading: boolean;
};

/**
 * FEAT-087: hook que decide si mostrar el modal bloqueante de BillingProfile.
 *
 * Se activa solo cuando:
 * 1. `session.status === "authenticated"`.
 * 2. El path actual no está en la lista de exclusión (auth/onboarding/payments).
 * 3. La query `getStatus` retornó `complete === false`.
 *
 * Mientras carga session o la query, retorna `isLoading: true` y `shouldShowGate: false`
 * para evitar flicker.
 */
export function useBillingProfileGate(): UseBillingProfileGateResult {
  const { status: sessionStatus } = useSession();
  const pathname = usePathname();

  const isAuthenticated = sessionStatus === "authenticated";
  const excluded = isExcludedPath(pathname);

  const { data: status, isLoading: isStatusLoading } = trpc.viewer.billingProfile.getStatus.useQuery(
    undefined,
    {
      enabled: isAuthenticated && !excluded,
      // Re-fetch al volver a foco para detectar perfiles recién creados desde otra pestaña.
      refetchOnWindowFocus: true,
      // Cache largo: el perfil cambia solo cuando el user lo edita o lo completa.
      staleTime: 5 * 60 * 1000,
    }
  );

  return useMemo(() => {
    if (!isAuthenticated || excluded) {
      return { shouldShowGate: false, isLoading: false };
    }
    if (isStatusLoading || !status) {
      return { shouldShowGate: false, isLoading: true };
    }
    return { shouldShowGate: !status.complete, isLoading: false };
  }, [isAuthenticated, excluded, isStatusLoading, status]);
}
